import { useRef, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import Button from '../../../../components/Button';
import {
  useGiftForm,
  handleKeyboardDone,
} from '../../../../components/GiftFormContext';
import { useSelector } from 'react-redux';
import { selectTokenByNetworkAndAddress } from '../../../../redux/slices/tokenSlice';
import TextInput from '../../../../components/Inputs/Text';
import { hapticsImpactLight } from '../../../../common/haptic';
import { Controller } from 'react-hook-form';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import {
  BodySmall,
  ParagraphSmallText,
} from '../../../../components/Texts';
import { Label } from '../../../../components/Inputs/Text';
import LinkIcon from '../../../../assets/icons/link.svg?react';
import MessageIcon from '../../../../assets/icons/message.svg?react';
import PresentationalGift from '../../../../views/Gifts/Gift/PresentationalGift';
import StepWrapper from '../../../../components/StepWrapper';
import { StaggerContainer, StaggerItem } from '../../../../components/Stagger';
import { useGetCurrentUserProfileQuery } from '../../../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import { isFutureDate } from '../../../../common/date';
import { useGiftAmountCalculations } from '../../../../hooks/useGiftAmountCalculations';

const PaymentMethodContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const PaymentMethodButton = styled.button`
  flex: 1;
  height: 62px;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  font-weight: 600;
  font-size: 16px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  position: relative;
`;

const PaymentMethodTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PaymentMethodText = styled(ParagraphSmallText)`
  font-weight: 600;
  line-height: 20px;
  text-align: left;
  color: ${({ theme }) => theme.text.title};
`;

const PaymentMethodSubtext = styled(BodySmall)`
  color: ${({ theme }) => theme.text.regular};
  text-align: left;
`;

const Preview = ({
  onContinue,
  buttonLabel = 'Continue',
  isBack = false,
}) => {
  const {
    usdAmount,
    image,
    setValue,
    targetTokenAddress,
    network,
    delivery,
    control,
    errors,
    email,
    message,
    targetToken,
    sendOnDate,
    isValid,
    amountSpecifiedInUsd,
    targetTokenAmount,
    selectedPaymentMethodType,
  } = useGiftForm();

  const targetTokenFromSelector = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const privyToken = useSelector(selectPrivyToken);

  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const displayToken = targetToken || targetTokenFromSelector;

  // Use the same calculation hook as Confirmation page
  const {
    formatedTokenAmountDisplay,
  } = useGiftAmountCalculations({
    network,
    targetTokenAddress,
    targetTokenAmount,
    usdAmount: amountSpecifiedInUsd ? usdAmount : undefined,
    amountSpecifiedInUsd: amountSpecifiedInUsd,
    delivery,
    selectedPaymentMethodType,
  });

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();

  const isFutureDelivery = sendOnDate && isFutureDate(sendOnDate);

  const handleDeliveryMethodChange = (method) => {
    hapticsImpactLight();
    setValue('delivery', method);
  };

  const isEmailRequired = delivery === 'email';

  const isValidForContinue = isEmailRequired
    ? email && email.trim() !== '' && !errors?.email?.message
    : true;

  const emailRef = useRef(null);

  return (
    <StepWrapper $back={isBack}>
      <StaggerContainer>
        <StaggerItem $delay={1}>
          <PresentationalGift
            usdAmount={amountSpecifiedInUsd ? usdAmount : undefined}
            tokenSymbol={displayToken?.symbol || ''}
            tokenAmount={
              !amountSpecifiedInUsd ? (formatedTokenAmountDisplay || targetTokenAmount) : formatedTokenAmountDisplay
            }
            token={displayToken}
            giftCardImageUrl={image}
            animated={false}
            email={currentUser?.email}
            message={message}
            giftId='preview-gift'
            wrappingType='default'
            hideText={true}
            hideButton={true}
            status={null}
            isCreator={false}
            isReceiver={false}
            revealText='Preview Gift'
          />
        </StaggerItem>
        {!isFutureDelivery && (
          <StaggerItem $delay={3}>
            <div style={{ width: '100%' }}>
              <Label style={{ marginTop: 16 }}>Delivery Method</Label>
              <PaymentMethodContainer>
                <PaymentMethodButton
                  $active={delivery === 'link'}
                  onClick={() => handleDeliveryMethodChange('link')}
                >
                  {!isMobile && (
                    <LinkIcon width={20} height={20} color={theme.text.regular} />
                  )}
                  <PaymentMethodTextContainer>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      {isMobile && (
                        <LinkIcon
                          width={20}
                          height={20}
                          color={theme.text.regular}
                        />
                      )}
                      <PaymentMethodText>Sharable Link</PaymentMethodText>
                    </div>
                    <PaymentMethodSubtext>Send via link</PaymentMethodSubtext>
                  </PaymentMethodTextContainer>
                </PaymentMethodButton>
                <PaymentMethodButton
                  $active={delivery === 'email'}
                  onClick={() => handleDeliveryMethodChange('email')}
                >
                  {!isMobile && (
                    <MessageIcon
                      width={20}
                      height={20}
                      color={theme.text.regular}
                    />
                  )}
                  <PaymentMethodTextContainer>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      {isMobile && (
                        <MessageIcon
                          width={20}
                          height={20}
                          color={theme.text.regular}
                        />
                      )}
                      <PaymentMethodText>Email</PaymentMethodText>
                    </div>
                    <PaymentMethodSubtext>
                      Send to an email address
                    </PaymentMethodSubtext>
                  </PaymentMethodTextContainer>
                </PaymentMethodButton>
              </PaymentMethodContainer>
            </div>
          </StaggerItem>
        )}
        {(delivery === 'email' || isFutureDelivery) && (
          <StaggerItem $delay={5}>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextInput
                  ref={(el) => {
                    field.ref(el);
                    emailRef.current = el;
                  }}
                  label='Recipient Email'
                  inputMode='email'
                  type='email'
                  placeholder='recipient@example.com'
                  error={errors?.email?.message}
                  enterkeyhint='done'
                  onKeyDown={(e) =>
                    handleKeyboardDone(e, emailRef, {
                      onProceed: onContinue,
                      isValidForm: isValidForContinue,
                    })
                  }
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
          </StaggerItem>
        )}
        <StaggerItem $delay={7} style={{ marginTop: '20px', width: '100%' }}>
          <Button
            fullWidth
            clickAnimation='bouncy'
            onClick={onContinue}
            disabled={!isValidForContinue}
          >
            {buttonLabel}
          </Button>
        </StaggerItem>
      </StaggerContainer>
    </StepWrapper>
  );
};

export default Preview;
